import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Button,
  Container,
  Icon,
  type IconProps,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';

interface AccordionData {
  title: string;
  details: string[];
  links?: {
    label: string;
    href: string;
    icon: IconProps['iconName'];
  }[];
}

const accordions: AccordionData[] = [
  {
    title: 'Expedited passport and visa services',
    details: [
      'Expedited passport and visa services are only available online. Travel advisors cannot answer questions about passport and visa requirements, fill out any passport applications, or provide advice on how to complete the forms.',
      `AAA's preferred partner, RushMyPassport, is an expert in passport services and can assist you with your passport or visa application, as well as inform you of all of the fees involved in getting both non-expedited and expedited passports.`,
    ],
    links: [
      {
        label: 'Learn more',
        href: 'https://www.ace.aaa.com/travel/information/passport-information-and-services.html',
        icon: 'plus-regular',
      },
    ],
  },
  {
    title: 'International driving permit',
    details: [
      'International driving permits (IDP) are available at AAA branches the same day, or you can apply for one online.',
    ],
    links: [
      {
        label: 'Find a branch',
        href: 'https://www.ace.aaa.com/locations.html',
        icon: 'hotel-regular',
      },
      {
        label: 'Get an IDP online',
        href: 'https://www.aaa-idp.com/step-1',
        icon: 'id-card-regular',
      },
      {
        label: 'Learn more',
        href: 'https://www.ace.aaa.com/travel/information/international-driving-permit.html',
        icon: 'plus-regular',
      },
    ],
  },
  {
    title: 'TripTik',
    details: [
      'TripTik travel planner is an interactive road trip planning tool that helps you find points of interest, gas stations, restaurants, hotels, and more along your route. You can get a TripTik in a branch, online, or via the Auto Club App.',
    ],
    links: [
      {
        label: 'Learn more',
        href: 'https://www.ace.aaa.com/travel/plan-a-road-trip/maps-and-directions.html#triptiks',
        icon: 'plus-regular',
      },
    ],
  },
  {
    title: 'Mexico auto insurance',
    details: [
      'Mexico car insurance policies offered through AAA are available for members to purchase at branches in California, New Mexico, and Texas.',
    ],
    links: [
      {
        label: 'Find a branch',
        href: 'https://www.ace.aaa.com/locations.html',
        icon: 'hotel-regular',
      },
    ],
  },
  {
    title: 'Foreign currency exchange',
    details: [
      'Purchasing foreign currency through AAA is fast, easy, and convenient with more than 80 currencies at competitive rates.',
    ],
    links: [
      {
        label: 'Learn more',
        href: 'https://ace.aaa.com/financial/foreign-currency.html',
        icon: 'plus-regular',
      },
    ],
  },
];

const RelevanceAccordions: React.FC = () => {
  return (
    <Container maxWidth='lg' data-testid='relevance-accordions-container'>
      <Stack
        direction='column'
        spacing={4}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          pb: {
            xs: 10,
            lg: 4,
          },
          width: '100%',
        }}
      >
        <Typography variant='h5'>
          Travel advisors do not accept appointments for the following services:
        </Typography>
        <AccordionGroup
          slotProps={{
            stackProps: {
              sx: {
                width: '100%',
                backgroundColor: 'white',
                borderStyle: 'solid',
                borderWidth: '0 1px',
                borderColor: 'grey.300',
                borderRadius: '8px',
                overflow: 'hidden',
              },
            },
          }}
        >
          {accordions.map((accordion) => (
            <Accordion
              key={accordion.title}
              slotProps={{
                root: {
                  sx: {
                    maxWidth: 'none',
                    width: '100%',
                  },
                },
              }}
            >
              <AccordionSummary sx={{}} variant='secondary'>
                <Typography variant='b2Regular'>{accordion.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction='column' spacing={2}>
                  {accordion.details.map((detail, index) => (
                    <Typography
                      // biome-ignore lint/suspicious/noArrayIndexKey: Using index as key for simplicity
                      key={index}
                      variant='b2Regular'
                      sx={{ textAlign: 'left', mb: 1 }}
                    >
                      {detail}
                    </Typography>
                  ))}
                  <Stack direction='column' spacing={1}>
                    {accordion.links?.map((link) => (
                      <Button
                        key={link.href}
                        variant='outlined'
                        label={link.label}
                        href={link.href}
                        {...{ target: '_blank' }}
                        sx={{
                          color: 'primary.700',
                          borderColor: 'primary.700',
                          borderRadius: '25px',
                          width: 'fit-content',
                          '&:hover': {
                            color: 'primary.800',
                            borderColor: 'primary.800',
                          },
                        }}
                        endIcon={<Icon iconName={link.icon} />}
                      />
                    ))}
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionGroup>
      </Stack>
    </Container>
  );
};

export default RelevanceAccordions;
